import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI, analyticsAPI, leadAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Plus,
  Menu,
  X,
  Eye,
  UserPlus,
  MousePointerClick,
  TrendingUp,
  ChevronRight,
  Calendar,
  ShoppingBag,
  ClipboardList
} from 'lucide-react';

const PROFILE_COLORS = {
  green: { bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-700', hex: '#22C55E' },
  blue: { bg: 'bg-blue-500', light: 'bg-blue-100 text-blue-700', hex: '#3B82F6' },
  red: { bg: 'bg-red-500', light: 'bg-red-100 text-red-700', hex: '#EF4444' },
  yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-100 text-yellow-700', hex: '#EAB308' },
  purple: { bg: 'bg-purple-500', light: 'bg-purple-100 text-purple-700', hex: '#A855F7' },
  black: { bg: 'bg-slate-900', light: 'bg-slate-200 text-slate-700', hex: '#171717' },
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profilesRes, analyticsRes, leadsRes] = await Promise.all([
          profileAPI.list(),
          analyticsAPI.get(),
          leadAPI.list(),
        ]);
        setProfiles(profilesRes.data);
        setAnalytics(analyticsRes.data);
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard', active: true },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  const stats = [
    { label: 'Total Taps', value: analytics?.total_taps || 0, icon: <MousePointerClick className="w-5 h-5" />, color: 'text-indigo-600 bg-indigo-100' },
    { label: 'Profile Views', value: analytics?.unique_visitors || 0, icon: <Eye className="w-5 h-5" />, color: 'text-emerald-600 bg-emerald-100' },
    { label: 'Leads Captured', value: analytics?.leads_count || 0, icon: <UserPlus className="w-5 h-5" />, color: 'text-amber-600 bg-amber-100' },
    { label: 'Contact Saves', value: analytics?.contact_saves || 0, icon: <TrendingUp className="w-5 h-5" />, color: 'text-purple-600 bg-purple-100' },
  ];

  const availableColors = Object.keys(PROFILE_COLORS).filter(
    color => !profiles.some(p => p.color === color)
  );

  const handleCreateProfile = async (color) => {
    try {
      const response = await profileAPI.create({
        profile_name: `My ${color.charAt(0).toUpperCase() + color.slice(1)} Profile`,
        color: color,
      });
      setProfiles([...profiles, response.data]);
      toast.success('Profile created!');
      navigate(`/profiles/${response.data.profile_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Signa
            </span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="dashboard-content">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <span className="text-sm text-slate-500 hidden sm:block">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'company_admin' ? 'Admin' : 'Employee'}
              </span>
            </div>
          </div>
        </header>

        {/* Dashboard content */}
        <main className="p-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color} mb-3`}>
                  {stat.icon}
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Profiles Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Profiles
              </h2>
              <p className="text-sm text-slate-500">{profiles.length}/6 profiles</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(PROFILE_COLORS).map(([color, styles]) => {
                const profile = profiles.find(p => p.color === color);
                return (
                  <div
                    key={color}
                    className={`color-card ${profile ? 'cursor-pointer' : ''}`}
                    onClick={() => profile && navigate(`/profiles/${profile.profile_id}`)}
                    data-testid={`profile-card-${color}`}
                  >
                    <div className={`w-full aspect-square rounded-lg ${styles.bg} mb-3 flex items-center justify-center`}>
                      {profile ? (
                        <span className="text-white font-bold text-lg">
                          {profile.profile_name?.charAt(0).toUpperCase()}
                        </span>
                      ) : (
                        <Plus className="w-6 h-6 text-white/50" />
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {profile?.profile_name || `${color.charAt(0).toUpperCase() + color.slice(1)}`}
                    </p>
                    <p className={`text-xs ${profile ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {profile ? 'Active' : 'Empty'}
                    </p>
                    {!profile && availableColors.includes(color) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full mt-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateProfile(color);
                        }}
                        data-testid={`create-profile-${color}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Create
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Leads */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent Leads
              </h2>
              <Link to="/leads" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                View all
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {recentLeads.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No leads captured yet</p>
                <p className="text-sm text-slate-400">Share your profile to start capturing leads</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentLeads.map((lead) => (
                  <div key={lead.lead_id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`lead-${lead.lead_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        <p className="text-sm text-slate-500">{lead.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </p>
                        {lead.contacted && (
                          <span className="text-xs text-emerald-600">Contacted</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
